/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef } from 'react';

import { FormattedMessage, FormattedRelativeTime, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { css, cssMap, jsx } from '@atlaskit/css';
import PullRequestIcon from '@atlaskit/icon/core/pull-request';
import { BitbucketIcon } from '@atlaskit/logo';
import Lozenge from '@atlaskit/lozenge';
import { Anchor, Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	DevOpsPullRequestStatus,
	type HydratedPullRequest,
} from '@atlassian/search-client/hydration/pr-types';

import { AzureDevopsIcon } from '../../constants/3p-product-configs/azure-devops/icon';
import { GithubIcon } from '../../constants/3p-product-configs/github/icon';
import { GitlabIcon } from '../../constants/3p-product-configs/gitlab/icon';
import {
	ACTION_SUBJECT_PREFIX,
	DEFAULT_GAS_CHANNEL,
	EVENT_SOURCE,
} from '../../constants/analytics';
import { getRelativeTimeSince } from '../../utils/search-result-utils';

import { messages } from './messages';

const iconContainerStyles = css({
	flexShrink: 0,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '16px',
	height: '16px',
});

type PullRequestProvider = 'github' | 'gitlab' | 'bitbucket' | 'azure-devops' | 'unknown';

const getProviderFromUrl = (url: string | null): PullRequestProvider => {
	if (!url) {
		return 'unknown';
	}
	try {
		const hostname = new URL(url).hostname.toLowerCase();
		if (hostname === 'github.com') {
			return 'github';
		}
		if (hostname === 'gitlab.com') {
			return 'gitlab';
		}
		if (hostname === 'bitbucket.org' || hostname.endsWith('stash.atlassian.com')) {
			return 'bitbucket';
		}
		if (hostname === 'dev.azure.com' || hostname.endsWith('.visualstudio.com')) {
			return 'azure-devops';
		}
		return 'unknown';
	} catch {
		return 'unknown';
	}
};

const ProviderIcon = ({ url }: { url: string | null }) => {
	const provider = getProviderFromUrl(url);
	switch (provider) {
		case 'github':
			return (
				<span css={iconContainerStyles}>
					<GithubIcon />
				</span>
			);
		case 'gitlab':
			return (
				<span css={iconContainerStyles}>
					<GitlabIcon />
				</span>
			);
		case 'azure-devops':
			return (
				<span css={iconContainerStyles}>
					<AzureDevopsIcon />
				</span>
			);
		case 'bitbucket':
			return (
				<span css={iconContainerStyles}>
					<BitbucketIcon label="Bitbucket" size="xsmall" />
				</span>
			);
		case 'unknown':
		default:
			return (
				<span css={iconContainerStyles}>
					<PullRequestIcon label="" size="small" color="currentColor" />
				</span>
			);
	}
};

const styles = cssMap({
	anchor: {
		textDecoration: 'none',
		color: token('color.link'),
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		'&:hover': {
			textDecoration: 'underline',
			color: token('color.link'),
		},
		'&:visited': {
			color: token('color.link.visited'),
		},
	},
	titleText: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	rowContainer: {
		paddingInlineStart: token('space.150'),
	},
	// 16px icon + 8px gap (space.100) = 24px = space.300
	metadata: {
		paddingInlineStart: token('space.300'),
	},
});

const lozengeContainerStyles = css({
	flexShrink: 0,
	// Fix height to match titleStyles lineHeight so that inline
	// children with built-in margins (e.g. Tag) do not cause the row to grow taller.
	height: '20px',
});

// Lozenge appearance mapping matches @atlaskit/smart-card's VALID_STATES
// See: linking-platform/smart-card/src/extractors/common/lozenge/utils.ts
const STATUS_APPEARANCE: Record<string, 'information' | 'success' | 'danger' | 'neutral'> = {
	[DevOpsPullRequestStatus.Open]: 'information',
	[DevOpsPullRequestStatus.Merged]: 'success',
	[DevOpsPullRequestStatus.Declined]: 'danger',
	[DevOpsPullRequestStatus.Draft]: 'information',
};

const STATUS_MESSAGES: Record<string, (typeof messages)[keyof typeof messages]> = {
	[DevOpsPullRequestStatus.Open]: messages.statusOpen,
	[DevOpsPullRequestStatus.Merged]: messages.statusMerged,
	[DevOpsPullRequestStatus.Declined]: messages.statusDeclined,
	[DevOpsPullRequestStatus.Draft]: messages.statusDraft,
};

const PullRequestRow = ({
	pr,
	position,
	onResultClicked,
}: {
	pr: HydratedPullRequest;
	position: number;
	onResultClicked?: () => void;
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const title = pr.title || 'Untitled';
	const prId = pr.pullRequestInternalId;
	const reviewerCount = pr.reviewers?.length ?? 0;
	const lastUpdated = pr.lastUpdated;
	const statusMessage = STATUS_MESSAGES[pr.status ?? ''] ?? messages.statusUnknown;

	const handlePRClick = () => {
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: `${ACTION_SUBJECT_PREFIX}LinkedPullRequest`,
			eventType: 'ui',
			source: EVENT_SOURCE,
			attributes: {
				position,
				pullRequestStatus: pr.status,
			},
		}).fire(DEFAULT_GAS_CHANNEL);
		onResultClicked?.();
	};

	return (
		<Box xcss={styles.rowContainer}>
			<Stack space="space.050">
				<Inline space="space.100" alignBlock="center">
					<ProviderIcon url={pr.url} />
					{pr.url ? (
						<Anchor href={pr.url} target="_blank" xcss={styles.anchor} onClick={handlePRClick}>
							{title}
						</Anchor>
					) : (
						<Box xcss={styles.titleText}>
							<Text size="small" weight="medium">
								{title}
							</Text>
						</Box>
					)}
					<span css={lozengeContainerStyles}>
						<Lozenge appearance={STATUS_APPEARANCE[pr.status ?? ''] ?? 'neutral'} isBold>
							{formatMessage(statusMessage)}
						</Lozenge>
					</span>
				</Inline>
				<Box xcss={styles.metadata}>
					<Inline
						separator={
							<Text size="small" color="color.text.subtlest">
								•
							</Text>
						}
						space="space.100"
						alignBlock="center"
					>
						<Text size="small" color="color.text.subtlest">
							<FormattedMessage {...messages.metadataLabel} />
						</Text>
						{prId && (
							<Text size="small" color="color.text.subtlest">
								<FormattedMessage {...messages.pullRequestId} values={{ prId }} />
							</Text>
						)}
						{lastUpdated && (
							<Text size="small" color="color.text.subtlest">
								{(() => {
									const { value, unit } = getRelativeTimeSince(new Date(lastUpdated));
									return (
										<FormattedMessage
											{...messages.lastUpdated}
											values={{
												relativeTime: <FormattedRelativeTime value={-value} unit={unit} />,
											}}
										/>
									);
								})()}
							</Text>
						)}
						<Text size="small" color="color.text.subtlest">
							<FormattedMessage {...messages.reviewerCount} values={{ count: reviewerCount }} />
						</Text>
					</Inline>
				</Box>
			</Stack>
		</Box>
	);
};

export const LinkedPullRequestsContent = ({
	pullRequests,
	onResultClicked,
}: {
	pullRequests: HydratedPullRequest[];
	onResultClicked?: () => void;
}): JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasFiredOpened = useRef(false);

	// Fires each time the PR section is expanded (component mounts).
	// ExpandList unmounts children on collapse, so the ref resets on remount — intentionally fires per-open, not once per session.
	// Uses a ref guard so it also handles the case where PRs load asynchronously after mount.
	useEffect(() => {
		if (pullRequests.length > 0 && !hasFiredOpened.current) {
			hasFiredOpened.current = true;
			createAnalyticsEvent({
				action: 'opened',
				actionSubject: `${ACTION_SUBJECT_PREFIX}LinkedPullRequestSection`,
				eventType: 'track',
				source: EVENT_SOURCE,
				attributes: {
					prCount: pullRequests.length,
				},
			}).fire(DEFAULT_GAS_CHANNEL);
		}
	}, [pullRequests, createAnalyticsEvent]);

	return (
		<Stack space="space.100">
			{pullRequests.map((pr, index) => (
				<PullRequestRow key={pr.id} pr={pr} position={index} onResultClicked={onResultClicked} />
			))}
		</Stack>
	);
};
